import { CardContainer } from "../../components/Card/CardContainer";
import Text from "../../components/Text/Text";
import { convertBuddhistYear, numberFormatter } from "../../utility/Formatter";
import moment from "moment";
import { Tabs } from "antd";
import OrderTab from "./TabsIndex/OrderTab";
import { useTabState } from "../../hook/useTabState";
import ModalCheckStatusCredit from "../../components/Modal/ModalCheckStatusCredit";
import { useCallback, useEffect, useMemo, useState } from "react";
import CreditTab from "./TabsIndex/CreditTab";
import {
  getOrderCreditLimit,
  getStatusRequestCredit,
  getTempCreditLimit,
} from "../../datasource/CreditLimitDatasource";
import {
  AllCreditLimitEntity_INIT,
  type AllCreditLimitEntity,
} from "../../entities/CreditLimitEntity";
import {
  AllOrderCreditLimitEntity_INIT,
  type AllOrderCreditLimitEntity,
  type creditApproversEntity,
  type orderCreditEntity,
  type soApproversEntity,
} from "../../entities/OrderCreditEntity";
import { useEffectOnce } from "react-use";
import { set } from "date-fns";

function CheckCreditLimit() {
  const initializeTab = () => {
    const existingTab = localStorage.getItem("creditTabs");
    if (!existingTab) {
      localStorage.setItem("creditTabs", "ORDER");
      return "ORDER";
    }
    return existingTab;
  };
  const company = JSON.parse(localStorage.getItem("company")!);
  const [activeTabKey, setActiveTabKey] = useState<string>(initializeTab());
  const orderTabState = useTabState();
  const [loading, setLoading] = useState(false);
  const [credit, setCredit] = useState<AllCreditLimitEntity>(AllCreditLimitEntity_INIT);
  const [order, setOrder] = useState<AllOrderCreditLimitEntity>(AllOrderCreditLimitEntity_INIT);
  const [approverCreditRequest, setApproverCreditRequest] = useState<{
    creditApprovers: creditApproversEntity | null;
    order: orderCreditEntity | null;
    soApprovers: soApproversEntity | null;
  }>({ creditApprovers: null, order: null, soApprovers: null });
  const [pendingCount, setPendingCount] = useState<number>(
    Number(localStorage.getItem("pendingCount")) || 0,
  );
  const [modalCheck, setModalCheck] = useState<any>();
  const fetchData = useCallback(async () => {
    const [startDate, endDate] = orderTabState.filterDate || [];
    const formatStartDate = startDate?.format("YYYY-MM-DD");
    const formatEndDate = endDate?.format("YYYY-MM-DD");
    if (activeTabKey === "CREDIT") {
      setLoading(true);
      try {
        const response = await getTempCreditLimit(
          company?.companyCode,
          orderTabState.searchText,
          formatStartDate,
          formatEndDate,
          orderTabState.status || "WAITING",
          orderTabState.sortField,
          orderTabState.sortDirection,
          orderTabState.page,
          10,
        );
        const newCount = response?.data?.filter((item) => item.status === "WAITING").length || 0;
        if (orderTabState?.status === "WAITING") {
          setPendingCount(newCount);
          localStorage.setItem("pendingCount", String(newCount));
        }
        setCredit(response);
      } catch (error) {
        console.error("Error fetching credit limit:", error);
      } finally {
        setLoading(false);
      }
    } else if (activeTabKey === "ORDER") {
      try {
        const response = await getOrderCreditLimit(
          company?.companyCode,
          orderTabState.status || "PENDING",
          orderTabState.searchText,
          orderTabState.waitApprove,
          formatStartDate,
          formatEndDate,
          orderTabState.sortField,
          orderTabState.sortDirection,
          orderTabState.page,
          10,
        );
        setOrder(response);
      } catch (error) {
        console.error("Error fetching order credit:", error);
      }
    }
  }, [activeTabKey, company?.companyCode, orderTabState]);

  useEffect(() => {
    orderTabState.reset();
    if (activeTabKey === "CREDIT" && orderTabState.status !== "WAITING") {
      orderTabState.setStatus("WAITING");
    } else if (activeTabKey === "ORDER" && orderTabState.status !== "PENDING") {
      orderTabState.setStatus("PENDING");
    }
  }, [activeTabKey]);

  useEffect(() => {
    fetchData();
  }, [
    activeTabKey,
    orderTabState.status,
    orderTabState.searchText,
    orderTabState.filterDate,
    orderTabState.sortDirection,
    orderTabState.page,
    orderTabState.waitApprove,
  ]);
  const checkApproveCredit = async () => {
    try {
      const response = await getStatusRequestCredit(modalCheck?.id);
      setApproverCreditRequest(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (modalCheck?.open) {
      checkApproveCredit();
    }
  }, [modalCheck]);
  const tabsItems = useMemo(
    () => [
      {
        label: (
          <Text fontSize={14} fontWeight={700}>
            คำสั่งซื้อ ({numberFormatter(order.countPending, 0) ?? 0})
          </Text>
        ),
        key: "ORDER",
        children: (
          <OrderTab
            key={1}
            data={order}
            {...orderTabState}
            loading={loading}
            setModalCheck={(val) => setModalCheck({ open: val?.open, id: val?.id })}
          />
        ),
      },
      ...(company.companyCode === "ICPL"
        ? []
        : [
            {
              label: (
                <Text fontSize={14} fontWeight={700}>
                  วงเงินชั่วคราว ({numberFormatter(pendingCount, 0) ?? 0})
                </Text>
              ),
              key: "CREDIT",
              children: (
                <CreditTab
                  key={2}
                  data={credit}
                  {...orderTabState}
                  loading={loading}
                  setModalCheck={setModalCheck}
                  modalCheck={modalCheck}
                  pendingCount={pendingCount}
                  fetchData={fetchData}
                />
              ),
            },
          ]),
    ],
    [order.count, credit.count, orderTabState, loading, modalCheck],
  );

  const handleTabChange = (key: string) => {
    setActiveTabKey(key);
    localStorage.setItem("creditTabs", key);
  };

  return (
    <CardContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Text fontFamily='IBM Plex Sans Thai' fontSize={24} fontWeight={700}>
          เช็คคำขอวงเงิน
        </Text>
        <Text fontFamily='Sarabun' fontSize={14} fontWeight={400}>
          {convertBuddhistYear.toBuddhistYear(
            moment(activeTabKey === "ORDER" ? order.lastUpdate : credit.lastUpdate),
            "อัปเดตล่าสุดเมื่อ DD/MM/YYYY , HH:mm",
          )}
        </Text>
      </div>
      <Tabs activeKey={activeTabKey} items={tabsItems} onChange={handleTabChange} />

      <ModalCheckStatusCredit
        open={modalCheck?.open}
        onClose={() => setModalCheck(undefined)}
        data={approverCreditRequest}
        companyCode={company?.companyCode}
      />
    </CardContainer>
  );
}

export default CheckCreditLimit;
